import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { draggingTypeWellChartIdAtom } from "atoms/arps";
import { JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME } from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import {
  BoxPlotDisplayMode,
  placesTypeWellsInCategories,
  resolveBoxPlotDisplayMode
} from "entities/charts/boxPlotDisplayMode";
import { categoryFromLabel } from "entities/charts/eva/translators/boxPlot";
import { useAtomValue } from "jotai";
import debounce from "lodash/debounce";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import { selectHasUnsavedChanges, useForecastStore } from "stores/forecast";
import { TypeWellSeriesData } from "types/charts/typeWellSeries";

import { useUserUnit } from "hooks";
import { useChartEntities } from "hooks/charts";
import { useEvaUnitCore } from "hooks/useEvaUnitCore";
import { useTypeWellUnitConversion } from "hooks/useTypeWellUnitConversion";

import { useTypeWellPerformanceQuery } from "api/typeWellPerformance";

import { INormalizeBySetting } from "models";
import { EntityKind } from "models/entityKind";

import { useFetchForecastArray } from "components/arps/hooks/useFetchForecastArray";
import handleTypeWellMissingDataToast from "components/arps/utils/handleTypeWellMissingDataToast";
import { arpsWasm } from "components/arps/utils/wasmLoader";
import { useScreenshotContext } from "components/screenshot/hooks";

import { uid } from "../../../utils";
import { ToastType, dismissChartToast, showChartToast } from "../components/ChartToast";
import { buildBoxPlotSeries, extractBoxPlotFields } from "./utils/boxPlotTypeWellBuilder";
import {
  buildCrossPlotSeries,
  extractCrossPlotFields
} from "./utils/crossPlotTypeWellBuilder";
import {
  buildNormalizeSettings,
  isValidDeclineChart,
  processTypeWellForDeclineChart
} from "./utils/declineChartTypeWellBuilder";

const EMPTY_ARRAY: TypeWellSeriesData = [];

/**
 * Hook to compute type well series data for charts.
 * Handles both decline charts (line series) and cross plots (scatter points).
 */
export default function useTypeWellSeries(
  checkedGlobalTypeWells: ICheckedForecast[],
  normalizeBySettings: INormalizeBySetting,
  useNormalizeBy: boolean,
  normalizeTypeWell: boolean,
  isTypeWellActive: boolean
) {
  const { id, entityKind } = useChartsContext();
  const {
    chartType,
    product: chartProduct,
    timeStep: timeStepState,
    apiResponse,
    chartTypeParam,
    jitterPlotToggle,
    violinPlotToggle,
    multiProduct,
    chartFocus
  } = useChartEntities(id);
  const isMultiProductActive = multiProduct?.active ?? false;
  const timeStep = timeStepState?.properties?.value;

  // Get global groupBy for box plots
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);

  const unitType = useUserUnit();
  const { isReady: isUnitCoreReady, convertUnitToUnit } = useEvaUnitCore();
  const { convertLengthToDisplay, convertProppantIntensityToDisplay } =
    useTypeWellUnitConversion();

  // State for decline chart series (computed via effect)
  const [declineTypeWellSeries, setDeclineTypeWellSeries] = useState<TypeWellSeriesData>(
    []
  );
  const [hasAlreadyDisplayedToast, setHasAlreadyDisplayedToast] = useState(false);
  const [
    numberOfTypeWellsMissingNormalization,
    setNumberOfTypeWellsMissingNormalization
  ] = useState(0);

  const hasUnsavedTypeWellChanges = useForecastStore(selectHasUnsavedChanges);

  //type well id only updates when the store value changes
  //this allows us to identify type wells with new data vs no changes
  const typeWellId = useMemo(() => uid(), [checkedGlobalTypeWells]);

  const { data: forecastArrays } = useFetchForecastArray({
    ids: Array.isArray(checkedGlobalTypeWells)
      ? checkedGlobalTypeWells.map((tw) => tw.id)
      : []
  });

  // ============================================================================
  // Cross Plot Support
  // ============================================================================

  const isCrossPlot = chartType === EvaChart.CrossPlot;

  const crossPlotFields = useMemo(() => {
    if (!isCrossPlot || !chartTypeParam?.properties) return [];
    return extractCrossPlotFields(
      chartTypeParam.properties.x?.property,
      chartTypeParam.properties.y?.property
    );
  }, [isCrossPlot, chartTypeParam?.properties]);

  const crossPlotPerformanceRequest = useMemo(() => {
    if (!isCrossPlot || !isTypeWellActive || crossPlotFields.length === 0) return null;
    const typeWellIds = checkedGlobalTypeWells?.map((tw) => tw.id) ?? [];
    if (typeWellIds.length === 0) return null;
    return { typeWellIds, fields: crossPlotFields };
  }, [isCrossPlot, isTypeWellActive, crossPlotFields, checkedGlobalTypeWells]);

  const { data: crossPlotPerformanceData, isLoading: isCrossPlotLoading } =
    useTypeWellPerformanceQuery(
      crossPlotPerformanceRequest,
      isCrossPlot && isTypeWellActive
    );

  const crossPlotResult = useMemo(() => {
    if (!isCrossPlot || !isTypeWellActive)
      return { series: [], missingCount: 0, missingDataCount: 0 };

    const xProperty = chartTypeParam?.properties?.x?.property;
    const yProperty = chartTypeParam?.properties?.y?.property;
    if (!xProperty || !yProperty)
      return { series: [], missingCount: 0, missingDataCount: 0 };

    // Apply unit conversions to normalization settings
    const sourceUnit = normalizeBySettings?.defaultUnit;
    const displayUnit = normalizeBySettings?.units?.[unitType];
    let convertedPer = normalizeBySettings?.per ?? 0;
    let convertedThreshold = normalizeBySettings?.threshold ?? 0;

    if (isUnitCoreReady && sourceUnit && displayUnit && sourceUnit !== displayUnit) {
      convertedPer = convertUnitToUnit(displayUnit, sourceUnit, convertedPer);
      convertedThreshold = convertUnitToUnit(displayUnit, sourceUnit, convertedThreshold);
    }

    return buildCrossPlotSeries({
      typeWells: checkedGlobalTypeWells,
      xProperty,
      yProperty,
      performanceData: crossPlotPerformanceData,
      normalization: normalizeBySettings
        ? {
            enabled: useNormalizeBy,
            normalizeTypeWell,
            field: normalizeBySettings.field ?? "",
            per: convertedPer,
            threshold: convertedThreshold,
            useMultilinearNormalization:
              normalizeBySettings.useMultilinearNormalization ?? false,
            higherScalar: normalizeBySettings.higherScalar ?? 0
          }
        : undefined,
      unitConversions: {
        convertLength: convertLengthToDisplay,
        convertProppantIntensity: convertProppantIntensityToDisplay
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- conversion functions are stable, only isUnitCoreReady matters
  }, [
    isCrossPlot,
    crossPlotPerformanceData,
    isTypeWellActive,
    checkedGlobalTypeWells,
    chartTypeParam?.properties,
    normalizeBySettings,
    useNormalizeBy,
    normalizeTypeWell,
    unitType,
    isUnitCoreReady
  ]);

  const crossPlotTypeWellSeries = crossPlotResult.series;
  const crossPlotMissingNormalizationCount = crossPlotResult.missingCount;
  const crossPlotMissingDataCount = crossPlotResult.missingDataCount;

  // ============================================================================
  // Box Plot Support
  // ============================================================================

  const isBoxPlot = chartType === EvaChart.BoxPlot;
  const boxPlotDisplayMode = resolveBoxPlotDisplayMode({
    jitterActive: jitterPlotToggle?.active,
    violinActive: violinPlotToggle?.active
  });
  const placesInCategories = placesTypeWellsInCategories(boxPlotDisplayMode);
  // An active chart focus replaces the global groupBy in the request (getGroupBySetting and
  // getChartFieldInformation both substitute it), so the categories that come back describe
  // the focus field. A type well has to be resolved against whichever of the two the response
  // was actually grouped by, or its value is matched against another field's bins.
  const activeGroupBy = chartFocus?.active
    ? chartFocus?.properties?.groupBy
    : globalGroupBy;
  const groupByProperty = activeGroupBy?.property ?? "";

  // Jitter is the only mode with its own x-axis field, taken from chartTypeParam. The
  // violin and the plain box both group by the groupBy field, so that is what a type well
  // has to be resolved against.
  const isJitterMode = boxPlotDisplayMode === BoxPlotDisplayMode.Jitter;
  const boxPlotXProperty = isJitterMode
    ? chartTypeParam?.properties?.x?.property ?? ""
    : groupByProperty;
  // The data type decides whether a value can be matched against the categories at all, and
  // so whether the field is worth asking the performance endpoint for: a text grouping never
  // resolves one, and an unsupported field can fail the whole query.
  const boxPlotXDataType = isJitterMode
    ? chartTypeParam?.properties?.x?.dataType
    : activeGroupBy?.dataType;

  const boxPlotFields = useMemo(() => {
    if (!isBoxPlot || !chartTypeParam?.properties) return [];
    return extractBoxPlotFields(
      chartTypeParam.properties.y?.property,
      boxPlotXProperty,
      boxPlotDisplayMode,
      boxPlotXDataType
    );
  }, [
    isBoxPlot,
    chartTypeParam?.properties,
    boxPlotXProperty,
    boxPlotXDataType,
    boxPlotDisplayMode
  ]);

  const boxPlotPerformanceRequest = useMemo(() => {
    if (!isBoxPlot || !isTypeWellActive) return null;

    // If no fields to fetch (e.g., both Y and X are date fields), skip
    if (boxPlotFields.length === 0) return null;

    const typeWellIds = checkedGlobalTypeWells?.map((tw) => tw.id) ?? [];
    if (typeWellIds.length === 0) return null;

    return { typeWellIds, fields: boxPlotFields };
  }, [isBoxPlot, isTypeWellActive, boxPlotFields, checkedGlobalTypeWells]);

  const { data: boxPlotPerformanceData, isLoading: isBoxPlotLoading } =
    useTypeWellPerformanceQuery(boxPlotPerformanceRequest, isBoxPlot && isTypeWellActive);

  // Extract the x-axis categories from apiResponse for the modes that place type wells in
  // them. The "(count) " prefix is stripped through the same helper the x-axis categories
  // are built with, so the category a type well is placed in always matches the axis
  // category even when the whitespace after the count is irregular.
  const boxPlotCategories = useMemo(() => {
    if (!isBoxPlot || !placesInCategories || !apiResponse?.series) return [];

    return (apiResponse.series as Array<{ label?: string; type?: string }>)
      .filter(
        (s) =>
          s.type === "box" && s.label && s.label !== JITTER_PLOT_LEGEND_ITEMS_SERIES_NAME
      )
      .map((s) => categoryFromLabel(s.label))
      .filter((label) => label.length > 0);
  }, [isBoxPlot, placesInCategories, apiResponse?.series]);

  const boxPlotResult = useMemo(() => {
    if (!isBoxPlot || !isTypeWellActive)
      return {
        series: [],
        missingCount: 0,
        missingDataCount: 0,
        placedInCategories: false
      };

    const yProperty = chartTypeParam?.properties?.y?.property;
    if (!yProperty)
      return {
        series: [],
        missingCount: 0,
        missingDataCount: 0,
        placedInCategories: false
      };

    // Apply unit conversions to normalization settings
    const sourceUnit = normalizeBySettings?.defaultUnit;
    const displayUnit = normalizeBySettings?.units?.[unitType];
    let convertedPer = normalizeBySettings?.per ?? 0;
    let convertedThreshold = normalizeBySettings?.threshold ?? 0;

    if (isUnitCoreReady && sourceUnit && displayUnit && sourceUnit !== displayUnit) {
      convertedPer = convertUnitToUnit(displayUnit, sourceUnit, convertedPer);
      convertedThreshold = convertUnitToUnit(displayUnit, sourceUnit, convertedThreshold);
    }

    return buildBoxPlotSeries({
      typeWells: checkedGlobalTypeWells,
      yProperty,
      xProperty: boxPlotXProperty,
      xDataType: boxPlotXDataType,
      displayMode: boxPlotDisplayMode,
      performanceData: boxPlotPerformanceData,
      normalization: normalizeBySettings
        ? {
            enabled: useNormalizeBy,
            normalizeTypeWell,
            field: normalizeBySettings.field ?? "",
            per: convertedPer,
            threshold: convertedThreshold,
            useMultilinearNormalization:
              normalizeBySettings.useMultilinearNormalization ?? false,
            higherScalar: normalizeBySettings.higherScalar ?? 0
          }
        : undefined,
      unitConversions: {
        convertLength: convertLengthToDisplay,
        convertProppantIntensity: convertProppantIntensityToDisplay
      },
      categories: boxPlotCategories
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- conversion functions are stable, only isUnitCoreReady matters
  }, [
    isBoxPlot,
    boxPlotPerformanceData,
    isTypeWellActive,
    checkedGlobalTypeWells,
    chartTypeParam?.properties,
    boxPlotXProperty,
    boxPlotXDataType,
    boxPlotDisplayMode,
    normalizeBySettings,
    useNormalizeBy,
    normalizeTypeWell,
    unitType,
    isUnitCoreReady,
    boxPlotCategories
  ]);

  const boxPlotTypeWellSeries = boxPlotResult.series;
  const boxPlotMissingNormalizationCount = boxPlotResult.missingCount;
  const boxPlotMissingDataCount = boxPlotResult.missingDataCount;
  const boxPlotPlacedInCategories = boxPlotResult.placedInCategories;

  // ============================================================================
  // Decline Chart Support
  // ============================================================================

  const updateDeclineSeries = useCallback(() => {
    if (
      !arpsWasm ||
      !checkedGlobalTypeWells ||
      !chartProduct ||
      !isValidDeclineChart(chartType) ||
      entityKind === EntityKind.Facility ||
      isMultiProductActive
    ) {
      // Only update state if not already empty to avoid infinite re-render loop
      setDeclineTypeWellSeries((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Build normalize settings with unit conversions
    const sourceUnit = normalizeBySettings?.defaultUnit;
    const displayUnit = normalizeBySettings?.units?.[unitType];
    let convertedPer = normalizeBySettings?.per ?? 0;
    let convertedThreshold = normalizeBySettings?.threshold ?? 0.0;

    if (isUnitCoreReady && sourceUnit && displayUnit && sourceUnit !== displayUnit) {
      convertedPer = convertUnitToUnit(displayUnit, sourceUnit, convertedPer);
      convertedThreshold = convertUnitToUnit(displayUnit, sourceUnit, convertedThreshold);
    }

    const normalizeSettings = buildNormalizeSettings(
      normalizeBySettings,
      useNormalizeBy,
      normalizeTypeWell,
      convertedPer,
      convertedThreshold
    );

    const context = {
      arpsWasm,
      chartType: chartType as EvaChart,
      chartProduct,
      timeStep: timeStep as TimeStep,
      entityKind,
      typeWellId,
      yAxisTitle: apiResponse?.layout?.yAxis?.title ?? "",
      forecastArrays,
      normalizeSettings,
      useNormalizeBy
    };

    const allSeries: TypeWellSeriesData = [];
    let totalMissingCount = 0;

    for (const tw of checkedGlobalTypeWells) {
      try {
        let result: { items: TypeWellSeriesData; missingCount: number };

        // Redux preserves references for unchanged type wells during drag.
        // Serve all other type wells from cache to avoid redundant WASM calls
        // every rAF frame.
        if (isDraggingRef.current) {
          const cached = seriesCacheRef.current.get(tw.id);
          if (cached && cached.typeWellRef === tw) {
            result = cached.result;
          } else {
            result = processTypeWellForDeclineChart(tw, context);
            seriesCacheRef.current.set(tw.id, { typeWellRef: tw, result });
          }
        } else {
          result = processTypeWellForDeclineChart(tw, context);
        }

        allSeries.push(...result.items);
        totalMissingCount += result.missingCount;
      } catch (err) {
        Sentry.captureException(err);
        return;
      }
    }

    setNumberOfTypeWellsMissingNormalization(totalMissingCount);
    setDeclineTypeWellSeries(allSeries);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- convertUnitToUnit is a stable function, only isUnitCoreReady matters
  }, [
    checkedGlobalTypeWells,
    chartType,
    chartProduct,
    timeStep,
    entityKind,
    typeWellId,
    apiResponse?.layout?.yAxis?.title,
    forecastArrays,
    normalizeBySettings,
    useNormalizeBy,
    normalizeTypeWell,
    unitType,
    isUnitCoreReady,
    isMultiProductActive
  ]);

  const updateDeclineSeriesDebounced = useMemo(
    () => debounce(updateDeclineSeries, 150),
    [updateDeclineSeries]
  );

  // During a drag gesture, bypass the 150 ms debounce so the chart re-renders
  // on every rAF tick (~30 fps).  Outside of drag this effect does nothing
  // because isDraggingTypeWell is false, so checkedGlobalTypeWells changes
  // continue to flow through the debounced path below.
  // Per-type-well WASM result cache used only during drag.
  // Reuse only when both the complete type well and chart context are unchanged.
  const seriesCacheRef = useRef(
    new Map<
      string,
      {
        typeWellRef: ICheckedForecast;
        result: { items: TypeWellSeriesData; missingCount: number };
      }
    >()
  );

  // Mirror isDraggingTypeWell in a ref so updateDeclineSeries (a useCallback)
  // can read the current drag state without adding it to the dep array.
  const isDraggingRef = useRef(false);

  const isDraggingTypeWell = useAtomValue(draggingTypeWellChartIdAtom) === id;

  // Keep the ref in sync and clear the cache when the drag gesture ends so
  // stale entries never bleed into subsequent non-drag renders.
  useEffect(() => {
    isDraggingRef.current = isDraggingTypeWell;
    if (!isDraggingTypeWell) {
      seriesCacheRef.current.clear();
    }
  }, [isDraggingTypeWell]);

  useEffect(() => {
    seriesCacheRef.current.clear();
  }, [
    chartType,
    chartProduct,
    timeStep,
    entityKind,
    apiResponse?.layout?.yAxis?.title,
    forecastArrays,
    normalizeBySettings,
    useNormalizeBy,
    normalizeTypeWell,
    unitType,
    isUnitCoreReady,
    isMultiProductActive
  ]);

  useEffect(() => {
    if (!isDraggingTypeWell) return;
    updateDeclineSeries();
  }, [checkedGlobalTypeWells, isDraggingTypeWell, updateDeclineSeries]);

  useEffect(() => {
    updateDeclineSeriesDebounced();
    return () => {
      updateDeclineSeriesDebounced.cancel();
    };
  }, [
    typeWellId,
    hasUnsavedTypeWellChanges,
    normalizeTypeWell,
    chartType,
    chartProduct,
    timeStep,
    useNormalizeBy,
    normalizeBySettings,
    apiResponse?.layout?.yAxis?.title,
    forecastArrays,
    updateDeclineSeriesDebounced
  ]);

  // ============================================================================
  // Toast Notifications
  // ============================================================================

  useEffect(() => {
    const showingOnlyTypeWells =
      apiResponse?.series?.length === 0 &&
      declineTypeWellSeries.length !== 0 &&
      isTypeWellActive;

    if (showingOnlyTypeWells && !hasAlreadyDisplayedToast) {
      const toastId = `type-well-only-${id}`;
      dismissChartToast(id, toastId);
      showChartToast(id, "No wells in map - Showing only type wells", ToastType.Warning, {
        toastId
      });
      setHasAlreadyDisplayedToast(true);
    } else if (!showingOnlyTypeWells && hasAlreadyDisplayedToast) {
      setHasAlreadyDisplayedToast(false);
    }
  }, [
    declineTypeWellSeries,
    apiResponse,
    hasAlreadyDisplayedToast,
    isTypeWellActive,
    id
  ]);

  // Show toast when type wells are missing data for required fields (cross plot or box plot)
  // Skip showing toast while data is still loading to avoid false positives
  // Skip showing toast during screenshot mode to keep screenshots clean
  const { widget } = useScreenshotContext();
  const isScreenshotActive = !!widget;

  useEffect(() => {
    if (!isTypeWellActive || isMultiProductActive) return;
    if (isCrossPlot && isCrossPlotLoading) return;
    if (isBoxPlot && isBoxPlotLoading) return;
    if (isScreenshotActive) return;

    let missingDataCount = 0;
    if (isCrossPlot) {
      missingDataCount = crossPlotMissingDataCount;
    } else if (isBoxPlot) {
      missingDataCount = boxPlotMissingDataCount;
    }

    handleTypeWellMissingDataToast(id, missingDataCount);
  }, [
    id,
    isTypeWellActive,
    isCrossPlot,
    isBoxPlot,
    isCrossPlotLoading,
    isBoxPlotLoading,
    crossPlotMissingDataCount,
    boxPlotMissingDataCount,
    isScreenshotActive
  ]);

  // ============================================================================
  // Return Value
  // ============================================================================

  // Only the box plot can place type wells in x-axis categories, and only in the modes and
  // groupings where a category actually resolves — the builder reports which it did.
  if (!isTypeWellActive || isMultiProductActive) {
    return {
      typeWellSeries: EMPTY_ARRAY,
      numberOfTypeWellsMissingNormalization: 0,
      typeWellsPlacedInCategories: false
    };
  }

  if (isCrossPlot) {
    return {
      typeWellSeries: crossPlotTypeWellSeries,
      numberOfTypeWellsMissingNormalization: crossPlotMissingNormalizationCount,
      typeWellsPlacedInCategories: false
    };
  }

  if (isBoxPlot) {
    return {
      typeWellSeries: boxPlotTypeWellSeries,
      numberOfTypeWellsMissingNormalization: boxPlotMissingNormalizationCount,
      typeWellsPlacedInCategories: boxPlotPlacedInCategories
    };
  }

  return {
    typeWellSeries: declineTypeWellSeries,
    numberOfTypeWellsMissingNormalization,
    typeWellsPlacedInCategories: false
  };
}
